import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, BookOpen } from 'lucide-react';
import { useQuery } from 'react-query';
import { parentApi } from '../../lib/parentApi';
import { Button, ListToolbar, Tile, TileContent } from '../../components/ui';

function mondayOf(date: Date) {
  const value = new Date(date);
  const day = value.getDay();
  const diff = (day + 6) % 7;
  value.setDate(value.getDate() - diff);
  value.setHours(0, 0, 0, 0);
  return value;
}

function addDays(date: Date, days: number) {
  const value = new Date(date);
  value.setDate(value.getDate() + days);
  return value;
}

function toDateString(date: Date) {
  return date.toISOString().slice(0, 10);
}

export default function ParentSchedule() {
  const [selectedUnid, setSelectedUnid] = useState('');
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const { data: children = [] } = useQuery(['parent-children'], () => parentApi.children());

  useEffect(() => {
    if (!selectedUnid && children.length > 0) {
      setSelectedUnid(children[0].sophomorixUnid);
    }
  }, [children, selectedUnid]);

  const { data: entries = [] } = useQuery(
    ['parent-week', selectedUnid, toDateString(weekStart)],
    () => parentApi.week(selectedUnid, toDateString(weekStart), toDateString(addDays(weekStart, 6))),
    { enabled: Boolean(selectedUnid) }
  );

  const days = useMemo(() => [0, 1, 2, 3, 4].map((offset) => addDays(weekStart, offset)), [weekStart]);

  const byDay = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    for (const entry of entries) {
      const key = entry.start_at ? toDateString(new Date(entry.start_at)) : 'unscheduled';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(entry);
    }
    return grouped;
  }, [entries]);

  return (
    <div className="space-y-4">
      <ListToolbar
        title="Stundenplan"
        description="Wochenansicht aus dem Klassenbuch."
      />

      <Tile>
        <TileContent>
          <div className="mb-3">
            <label className="text-sm text-gray-700 dark:text-gray-300">Kind</label>
            <select
              className="input-field mt-1 min-h-[2.25rem] py-2 text-sm"
              value={selectedUnid}
              onChange={(event) => setSelectedUnid(event.target.value)}
            >
              {children.map((child) => (
                <option key={child.sophomorixUnid} value={child.sophomorixUnid}>
                  {child.Vorname} {child.Name} ({child.Klasse})
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4 flex items-center justify-end gap-1">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setWeekStart(addDays(weekStart, -7))}
            >
              <ChevronLeft size={16} />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setWeekStart(mondayOf(new Date()))}
            >
              <CalendarDays size={14} /> Heute
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setWeekStart(addDays(weekStart, 7))}
            >
              <ChevronRight size={16} />
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
            {days.map((day) => {
              const key = toDateString(day);
              const list = byDay[key] || [];
              return (
                <div
                  key={key}
                  className="min-h-[180px] rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900"
                >
                  <div className="mb-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                    {day.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' })}
                  </div>
                  {list.length === 0 ? (
                    <div className="text-xs text-gray-400 dark:text-gray-500">Keine Stunden</div>
                  ) : (
                    <div className="space-y-1">
                      {list.map((entry) => (
                        <div key={entry.id} className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-800">
                          <div className="flex items-center gap-1 font-medium text-gray-900 dark:text-gray-100">
                            <BookOpen className="h-3.5 w-3.5" />
                            {entry.title}
                          </div>
                          {entry.topic_summary ? (
                            <div className="text-[11px] text-gray-600 dark:text-gray-300">{entry.topic_summary}</div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TileContent>
      </Tile>
    </div>
  );
}
